import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalButton } from './GlobalButton';
import { useTheme } from '../context/ThemeProvider';
import { useViewLayoutMode } from '../hooks/useViewLayoutMode';

type ViewToolbarProps = {
  title: string;
  backTo?: string;
  backLabel?: string;
  actions?: ReactNode;
};

export function ViewToolbar({ title, backTo, backLabel = 'Go back', actions }: ViewToolbarProps) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  function handleBack() {
    if (backTo) navigate(backTo);
  }

  return (
    <header className="view-toolbar">
      <div className="view-toolbar-start">
        {backTo ? (
          <GlobalButton
            type="button"
            variant="secondary"
            iconOnly
            onClick={handleBack}
            aria-label={backLabel}
          >
            <img
              src={isDarkMode ? '/assets/icons/chevron.left.svg' : '/assets/icons/chevron.left.black.svg'}
              alt=""
              width={12}
              height={12}
            />
          </GlobalButton>
        ) : null}
        <h1 className="view-toolbar-title">{title}</h1>
      </div>
      {actions ? <div className="view-toolbar-actions">{actions}</div> : null}
    </header>
  );
}

type ViewSectionHeaderProps = {
  eyebrow: string;
  title: string;
  subtitle?: string;
  children?: ReactNode;
  actions?: ReactNode;
};

export function ViewSectionHeader({
  eyebrow,
  title,
  subtitle,
  children,
  actions,
}: ViewSectionHeaderProps) {
  return (
    <header className="view-section-head">
      <div className="view-section-head-main">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="view-section-title">{title}</h2>
        {subtitle ? <p className="muted">{subtitle}</p> : null}
        {children}
      </div>
      {actions ? <div className="view-section-head-actions">{actions}</div> : null}
    </header>
  );
}

type ViewPageProps = {
  title: string;
  backTo?: string;
  backLabel?: string;
  actions?: ReactNode;
  children: ReactNode;
};

export function ViewPage({ title, backTo, backLabel, actions, children }: ViewPageProps) {
  const { mode: layoutMode } = useViewLayoutMode();

  return (
    <div className="view" data-layout={layoutMode}>
      <ViewToolbar title={title} backTo={backTo} backLabel={backLabel} actions={actions} />
      {children}
    </div>
  );
}
